import { formatNumber } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  API_PREFIX_V2,
  GlobalWebsocketService
} from '@approot/global-websocket';
import { PricingData } from '@approot/models/strategy-payload';
import {
  AlertsService,
  LegService,
  PricingExtraService,
  ProductsConfigService
} from '@approot/services';
import {
  exponentialDelay,
  shortUuid,
  sleep,
  SubscriptionSink,
  Utilities
} from '@approot/utilities';
import { fromEvent } from 'rxjs';
import { LegComponent } from '../../leg/leg.component';

interface WebSocketPayload {
  solverTime: number;
  solvedValue: number;
  body?: string;
}

@Component({
  selector: 'app-solver',
  templateUrl: './solver.component.html',
  styleUrls: ['./solver.component.scss']
})
export class SolverComponent implements OnInit, OnDestroy {
  @Input() legComponent: LegComponent;
  @Input() isModalOpened = false;
  @Input() multiSolverMode = false;
  @Output() solverOpenChanged = new EventEmitter<boolean>();
  @Output() applySolver = new EventEmitter<{
    type: string;
    value: number;
  }>();

  pricingData: PricingData;
  solverForm: FormGroup;
  crossValues: string[] = [];
  timeTaken: number = null; // holds the amount of time the solver took in milliseconds
  currentlySolving = false;
  private subSink = new SubscriptionSink();
  private webSocketUUID = 'solver_' + shortUuid();

  constructor(
    private legService: LegService,
    private webSocketService: GlobalWebsocketService,
    private productsConfigService: ProductsConfigService,
    private pricingExtraService: PricingExtraService,
    private alertService: AlertsService
  ) {}

  public ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  public ngOnInit(): void {
    this.initWebSocketListener();
    this.createSolverForm();
    let cross = this.legComponent.form.get('cross');
    if (cross && cross.value) {
      let baseCurrency = cross.value.substring(0, 3);
      let quoteCurrency = cross.value.substr(cross.value.length - 3);

      this.crossValues.push(baseCurrency);
      this.crossValues.push(quoteCurrency);
    }

    this.subSink.sink = fromEvent(document, 'keydown').subscribe(event => {
      // we want to listen to the f9 button press
      this.keypress(event as KeyboardEvent);
    });
  }

  private keypress(event: KeyboardEvent): void {
    if (
      event.code === 'F9' &&
      this.solverForm.valid &&
      !this.currentlySolving
    ) {
      // if the key is f9 and the forms are valid, price the dci
      this.onSolverSubmit();
    }
  }

  private initWebSocketListener(): void {
    const resubscribe = (iteration: number) => {
      this.currentlySolving = false;
      this.subSink.sink = this.webSocketService
        .getListener({
          action: `${API_PREFIX_V2}pricing`,
          uuid: this.webSocketUUID,
          take: Infinity
        })
        .subscribe({
          next: resp => {
            // errors go through the subscribe as throwing errors will result in the websocket dying
            this.currentlySolving = false;
            if (resp.error) {
              this.alertService.addAlert({
                message: resp.error.message,
                type: 'error'
              });
            } else {
              this.handleSolvedValue(resp.payload as WebSocketPayload);
            }
          },
          complete: async () => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          },
          error: async _ => {
            await sleep(exponentialDelay(iteration));
            resubscribe(iteration + 1);
          }
        });
    };
    resubscribe(0);
  }

  private handleSolvedValue(payload: WebSocketPayload): void {
    if (payload.solvedValue != null) {
      this.currentlySolving = false;
      this.timeTaken = payload.solverTime * 1000; // get the time it took the solver to complete in milliseconds
      let cross = '';
      if (this.solverForm.get('cross')) {
        cross = this.solverForm.get('cross').value;
      }
      let decimalPlacesFormat =
        this.productsConfigService.decimalPlacesForCrossPipe(cross);
      this.solverForm
        .get('solvedValue')
        .setValue(
          formatNumber(
            payload.solvedValue,
            'en-GB',
            decimalPlacesFormat
          ).replace(/,/g, '')
        );
    } else if (typeof payload.body === 'string') {
      this.alertService.addAlert({
        message: payload.body,
        type: 'error'
      });
    }
  }

  onNumericBlur(elementName) {
    let value = this.solverForm.get(elementName).value;
    if (
      new RegExp('^(?:([0-9]+([.][0-9]+)k|\\d{3}k|\\d{2}k|\\d{1}k))$').test(
        value
      )
    ) {
      this.solverForm
        .get(elementName)
        .setValue(Number(value.replace('k', '')) * 1000);
    } else if (
      new RegExp('^(?:([0-9]+([.][0-9]+)m|\\d{3}m|\\d{2}m|\\d{1}m))$').test(
        value
      )
    ) {
      this.solverForm
        .get(elementName)
        .setValue(Number(value.replace('m', '')) * 1000000);
    }
  }

  createSolverForm() {
    const group: any = {};

    group['targetField'] = new FormControl('', Validators.required);
    group['targetPrice'] = new FormControl('', Validators.required);
    group['inputField'] = new FormControl('', Validators.required);
    group['cross'] = new FormControl('', Validators.required);
    group['solvedValue'] = new FormControl('');

    this.solverForm = new FormGroup(group);
  }

  onSolverSubmit() {
    let task = {
      taskType: 'solver',
      taskParameters: {
        targetField: this.solverForm.get('targetField').value,
        targetPrice: Utilities.removeCommasConvertToNumber(
          this.solverForm.get('targetPrice').value
        ),
        targetCurrency: this.solverForm.get('cross').value,
        inputField: this.solverForm.get('inputField').value
      }
    };

    //join all the market data from all the legs
    this.pricingData = {
      fixings: [],
      treasuryRates: [],
      marketData: []
    };

    let fixings = [];

    if (this.legComponent.legHelperService.requiredFixings) {
      fixings = this.legComponent.legPricingService.getFixings();
    }

    this.pricingData.fixings = fixings;

    // get tenor values before pricing if DCD product
    //otherwise price with treasury rate curve
    this.price(task);
  }

  price(task) {
    let payload = this.legService.createServicePayload(
      this.pricingExtraService.getMarketDataTimeStamp,
      this.pricingExtraService.marketDataSpreadingRule,
      [
        {
          product: this.legComponent.form.value,
          attributes: this.legComponent.leg.attributes
        }
      ],
      task,
      this.pricingData
    );

    this.currentlySolving = true;
    this.webSocketService.sendMessage({
      action: `${API_PREFIX_V2}pricing`,
      payload,
      uuid: this.webSocketUUID
    });
  }

  cancel() {
    this.modalChanged(false);
  }

  modalChanged(event) {
    this.solverOpenChanged.emit(event);
  }

  applySolvedValue() {
    this.solverOpenChanged.emit(false);
    switch (this.solverForm.get('inputField').value) {
      case 'Strike':
        this.emitValue('strike');
        break;
      case 'StrikeShort':
        this.emitValue('strikeShort');
        break;
      case 'StrikeLong':
        this.emitValue('strikeLong');
        break;
      case 'Spot':
        this.emitValue('spot');
        break;
      case 'Barrier':
        this.emitValue('barrier');
        break;
      case 'LowerBarrier':
        this.emitValue('lowerBarrier');
        break;
      case 'UpperBarrier':
        this.emitValue('upperBarrier');
        break;
      case 'Leverage':
        this.emitValue('leverage');
        break;
      case 'LeverageShort':
        this.emitValue('leverageShort');
        break;
      case 'LeverageLong':
        this.emitValue('leverageLong');
        break;
    }
  }

  private emitValue(attributeName: string): void {
    const value =
      attributeName === 'strike' || attributeName === 'spot'
        ? this.productsConfigService.crossDecimalFormatter(
            this.solverForm.get('solvedValue').value,
            this.legComponent.form.get('cross').value
          )
        : this.solverForm.get('solvedValue').value;

    this.applySolver.emit({
      type: attributeName,
      value
    });
  }
}
